import React, { Dispatch, SetStateAction, useRef, useState } from 'react';
import classes from './BulkImportModal.module.scss';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { Portal } from '../../../components/containers/Portal';
import modalClasses from '../../../components/containers/Modal/Modal.module.scss';
import { SquareButton } from '../../../components/actions/SquareButton';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { BulkImportNavigationTabs } from '../BulkImportNavigationTabs';
import { IconsEnum } from '../../../constants/icons';
import { useDidMount } from '../../../hooks/useDidMount';
import { FieldValues, SubmitErrorHandler, useForm } from 'react-hook-form';
import { FormOptions } from '../../../constants/form';
import { BulkActionsState } from '../../../models/bulk-actions';
import { BulkImportContent } from '../BulkImportContent';
import { SubmitHandler } from 'react-hook-form/dist/types/form';

export type BulkImportModalViewProps = {
  setBulkImportModal: Dispatch<SetStateAction<boolean>>;
  onSubmit: SubmitHandler<FieldValues>;
  onError: SubmitErrorHandler<FieldValues>;
};

export const BulkImportModalView = React.forwardRef<HTMLDivElement, BulkImportModalViewProps>(
  (props, ref): JSX.Element => {
    const { setBulkImportModal, onError, onSubmit } = props;
    const [mount, setMount] = useState<boolean>(false);
    const cssRef = useRef<HTMLDivElement>(null);
    const [activeSection, setActiveSection] = useState<keyof BulkActionsState>('targeting');

    const modalWrapperClasses = classNames(classes.BulkImportModal, 'grid-container', 'full');
    const modalHeaderClasses = classNames(modalClasses.Header, classes.Header, 'grid-x', 'align-justify');
    const footerContainerClasses = classNames(classes.ButtonContainer, 'grid-x', 'align-right');

    useDidMount(() => {
      // we need to setMount to true, since we want to trigger animation (fade) only when the component has been completely mounted.
      setMount(true);
      return () => setMount(false);
    });

    const formInstance = useForm({ ...FormOptions });

    return (
      <Portal>
        <CSSTransition
          in={mount}
          timeout={300}
          classNames={'fade'}
          unmountOnExit
          onExited={() => setBulkImportModal(false)}
          nodeRef={cssRef}>
          <div className={modalWrapperClasses} ref={cssRef}>
            <div className={`grid-y align-center full-height`}>
              <div className={`grid-x align-center`}>
                <div className={classes.ModalContainer} ref={ref}>
                  <form onSubmit={formInstance.handleSubmit(onSubmit, onError)}>
                    <div className={modalHeaderClasses}>
                      <h3 className={modalClasses.Title}>Bulk Actions</h3>
                      <div className={modalClasses.CloseButtonContainer}>
                        <SquareButton
                          type={'transparent'}
                          icon={SquareButtonIconsEnum.close}
                          action={() => props.setBulkImportModal(false)}
                        />
                      </div>
                    </div>
                    <div className={classes.Content}>
                      <div className={'grid-x'}>
                        <div className={classes.LeftColumn}>
                          <BulkImportNavigationTabs
                            section={'targeting'}
                            icon={IconsEnum.target}
                            selected={activeSection === 'targeting'}
                            action={() => {
                              setActiveSection('targeting');
                            }}>
                            Post Targeting
                          </BulkImportNavigationTabs>
                          <BulkImportNavigationTabs
                            section={'linkSettings'}
                            icon={IconsEnum.link}
                            selected={activeSection === 'linkSettings'}
                            action={() => {
                              setActiveSection('linkSettings');
                            }}>
                            Link Settings
                          </BulkImportNavigationTabs>
                          <BulkImportNavigationTabs
                            section={'scheduling'}
                            icon={IconsEnum.multidate}
                            selected={activeSection === 'scheduling'}
                            action={() => {
                              setActiveSection('scheduling');
                            }}>
                            Scheduling
                          </BulkImportNavigationTabs>
                        </div>
                        <div className={classes.RightColumn} id={'bulk-actions-scrollable-content'}>
                          <BulkImportContent activeSection={activeSection} formInstance={formInstance} />
                        </div>
                      </div>
                    </div>
                    <div className={footerContainerClasses}>
                      <div className={'cell shrink'}>
                        <Button type={'submit'} variant={'solid'} size={'large'} color={ButtonColorsEnum.secondary}>
                          Apply Changes
                        </Button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </CSSTransition>
      </Portal>
    );
  }
);

BulkImportModalView.displayName = 'BulkImportModal';
